import {useCallback, useEffect, useState} from "react";
import {AlertTriangle, Ticket} from "lucide-react";
import {
  createAdminVoucher,
  deleteAdminVoucher,
  getAdminVoucherMeta,
  listAdminVouchers,
  updateAdminVoucher,
  type AdminVoucher,
  type AdminVoucherMeta
} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminToggleField, AdminRowTrailing} from "./ui";

const emptyForm = {
  activ: 0,
  nume: "",
  cod: "",
  tip: 1,
  tipar: 0,
  valoare: 0,
  data1: new Date().toISOString().slice(0, 10),
  data2: new Date().toISOString().slice(0, 10),
  categorie: [] as number[],
  subcategorie: [] as number[],
  brand: [] as number[]
};

export function VouchersView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const [items, setItems] = useState<AdminVoucher[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState<AdminVoucherMeta | null>(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminVouchers({page, pageSize: 20, search: search || undefined})
      .then((r) => { setItems(r.items); setTotalPages(r.totalPages); })
      .catch((e: Error) => setErr(e.message));
  }, [page, search]);
  useEffect(() => { load(); }, [page]);
  useEffect(() => {
    getAdminVoucherMeta().then(setMeta).catch(() => {});
  }, []);
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(v: AdminVoucher) {
    setOpenId(v.id);
    setForm({
      activ: v.activ,
      nume: v.nume,
      cod: v.cod,
      tip: v.tip,
      tipar: v.tipar,
      valoare: v.valoare,
      data1: v.data1,
      data2: v.data2,
      categorie: v.categorie,
      subcategorie: v.subcategorie,
      brand: v.brand
    });
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm({...emptyForm, data1: new Date().toISOString().slice(0, 10), data2: new Date().toISOString().slice(0, 10)});
    setModalOpen(true);
  }
  function toggleId(key: "categorie" | "subcategorie" | "brand", id: number) {
    setForm((f) => ({
      ...f,
      [key]: (f[key] as number[]).includes(id) ? (f[key] as number[]).filter((x) => x !== id) : [...(f[key] as number[]), id]
    }));
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          if(!form.nume.trim() || !form.cod.trim()) { adminToast.error("Name and code are required."); return; }
          await createAdminVoucher(form);
          adminToast.success("Voucher added.");
        } else if(typeof openId === "number") {
          await updateAdminVoucher(openId, form);
          adminToast.success("Voucher saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete voucher?", async () => {
      await deleteAdminVoucher(id);
      closeModal(false);
      load();
      adminToast.success("Voucher deleted.");
    });
  }
  function scopeFields(f: typeof form) {
    if(!meta) return null;
    if(f.tipar === 2) {
      return (
        <div className="admin-detail-panel__section">
          <b>Brand:</b>
          <div className="admin-scope-checkboxes">
            {meta.brands.map((b) => (
              <label key={b.id}>
                <input type="checkbox" checked={f.brand.includes(b.id)} onChange={() => toggleId("brand", b.id)} />
                {b.nume}
              </label>
            ))}
          </div>
        </div>
      );
    }
    if(f.tipar === 1) {
      return (
        <div className="admin-detail-panel__section">
          <b>Categorii:</b>
          <div className="admin-scope-checkboxes">
            {meta.categories.map((c) => (
              <label key={c.id}>
                <input type="checkbox" checked={f.categorie.includes(c.id)} onChange={() => toggleId("categorie", c.id)} />
                {c.nume}
              </label>
            ))}
          </div>
          <b>Subcategorii:</b>
          <div className="admin-scope-checkboxes">
            {meta.subcategories.map((s) => (
              <label key={s.id}>
                <input type="checkbox" checked={f.subcategorie.includes(s.id)} onChange={() => toggleId("subcategorie", s.id)} />
                {s.nume}
              </label>
            ))}
          </div>
        </div>
      );
    }
    return null;
  }
  function voucherFormFields() {
    return (
      <div className="admin-detail-panel__form">
        <AdminToggleField
          title="Active voucher"
          description="Inactive vouchers cannot be applied at checkout."
          checked={form.activ === 0}
          onCheckedChange={(v) => setForm({...form, activ: v ? 0 : 1})}
        />
        <AdminField label="Name">
          <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} placeholder="Name" />
        </AdminField>
        <AdminField label="Code">
          <Input value={form.cod} onChange={(e) => setForm({...form, cod: e.target.value})} placeholder="Code" />
        </AdminField>
        <AdminField label="Type">
          <Select value={String(form.tip)} onValueChange={(v) => setForm({...form, tip: Number(v)})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Valoare fixa</SelectItem>
              <SelectItem value="1">Procent</SelectItem>
            </SelectContent>
          </Select>
        </AdminField>
        <AdminField label="Typear">
          <Select value={String(form.tipar)} onValueChange={(v) => setForm({...form, tipar: Number(v)})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">General</SelectItem>
              <SelectItem value="1">Categorie</SelectItem>
              <SelectItem value="2">Brand</SelectItem>
            </SelectContent>
          </Select>
        </AdminField>
        <AdminField label="Value">
          <Input type="number" value={form.valoare} onChange={(e) => setForm({...form, valoare: Number(e.target.value)})} placeholder="Value" />
        </AdminField>
        <AdminField label="Start date">
          <Input value={form.data1} onChange={(e) => setForm({...form, data1: e.target.value})} placeholder="Start date" />
        </AdminField>
        <AdminField label="End date">
          <Input value={form.data2} onChange={(e) => setForm({...form, data2: e.target.value})} placeholder="End date" />
        </AdminField>
      </div>
    );
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title="Vouchers"
      description="Discount vouchers"
      breadcrumbs={[
        {label: "Dashboard", href: "/vadmin"},
        {label: "Vouchers"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add voucher" />}
    >
      {confirmDialog}
      <AdminModernTable
        items={items}
        getId={(v) => v.id}
        getTitle={(v) => v.nume}
        getSubtitle={(v) => v.cod}
        getMeta={(v) => (
          <>
            {v.inactive ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /></AdminBadge> : null}
            <AdminBadge variant="secondary">{v.tipLabel}</AdminBadge>
            <AdminBadge variant="outline">{v.tiparLabel}</AdminBadge>
          </>
        )}
        getSearchText={(v) => adminSearchText(
          v.id, v.nume, v.cod, v.tipLabel, v.tiparLabel, v.valoare, v.data1, v.data2, v.activ,
          v.categorie, v.subcategorie, v.brand,
          meta?.categories.map((c) => c.nume),
          meta?.subcategories.map((s) => s.nume),
          meta?.brands.map((b) => b.nume)
        )}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No vouchers"
        emptyDescription="Add a new voucher or adjust filters."
        searchPlaceholder="Search name, cod, tip..."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
        getRowActions={(v) => (
          <AdminRowTrailing onDelete={() => removeItem(v.id)} deleteLabel="Delete voucher" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New voucher" : "Edit voucher"}
        icon={Ticket}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        {voucherFormFields()}
        {scopeFields(form)}
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
